import { observer } from 'mobx-react-lite'
import type { ReactNode } from 'react'
import type { PersonalTaxRefundMethodSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/PersonalTaxRefundMethodSupportingItemViewModel'
import type { BusinessTaxRefundMethodSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/BusinessTaxRefundMethodSupportingItemViewModel'
import type { TaxRefundMethodVersion } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethod'
import { useSubject } from '@/hooks/rx-hooks'
import {
  TaxRefundMethodCardListSection,
  ProvidedTaxRefundMethodCardListEmptyState,
  OtherMethodDropdown,
  AddNewRefundMethodButton,
  DirectDepositMethodCardListItem,
  CheckMethodCardListItem,
  FutureTaxCreditMethodCardListItem,
  TaxRefundMethodCardListSectionSkeleton,
} from './index'

/**
 * Props for {@link TaxRefundMethodCardListSectionsContainer}.
 */
export interface TaxRefundMethodCardListSectionsContainerProps {
  /**
   * The view model.
   */
  vm:
    | PersonalTaxRefundMethodSupportingItemViewModel
    | BusinessTaxRefundMethodSupportingItemViewModel
  /**
   * Whether the entire section is disabled.
   */
  isDisabled?: boolean
}

/**
 * Container for the tax refund method sections.
 * Orchestrates the display of current and other available tax refund methods in separate sections.
 */
export const TaxRefundMethodCardListSectionsContainer = observer(
  function ProvidedTaxRefundMethodSectionsContainer({
    vm,
    isDisabled,
  }: TaxRefundMethodCardListSectionsContainerProps) {
    const currentMethod = vm.method
    const otherMethods = vm.otherAvailableMethods

    const addButton = (
      <AddNewRefundMethodButton
        onPress={() => vm.showEditorDialog(null)}
        isDisabled={isDisabled}
      />
    )

    if (vm.isFetchingEditorData) {
      return <TaxRefundMethodCardListSectionSkeleton />
    }

    return (
      <>
        <TaxRefundMethodCardListSection
          title="Current"
          action={vm.method ? addButton : null}
          renderEmptyState={() => (
            <ProvidedTaxRefundMethodCardListEmptyStateContainer
              vm={vm}
              action={addButton}
            />
          )}
        >
          {currentMethod && (
            <TaxRefundMethodCardListItemContainer
              method={currentMethod}
              isCurrent
              onAction={() => vm.showEditorDialog(currentMethod)}
              isDisabled={vm.provideMethod.pending || isDisabled}
            />
          )}
        </TaxRefundMethodCardListSection>

        {otherMethods.length > 0 && (
          <TaxRefundMethodCardListSection
            title={currentMethod ? 'Other' : 'Existing'}
          >
            {otherMethods.map((method) => (
              <OtherMethodCardListItemContainer
                key={method.id}
                method={method}
                onSelect={() => vm.provideMethod(method)}
                onDelete={() => vm.promptDeleteMethodPermanently(method)}
                isDisabled={vm.provideMethod.pending || isDisabled}
              />
            ))}
          </TaxRefundMethodCardListSection>
        )}
      </>
    )
  },
)

/**
 * Props for {@link TaxRefundMethodCardListItemContainer}.
 */
export interface TaxRefundMethodCardListItemContainerProps {
  /**
   * The tax refund method.
   */
  method: TaxRefundMethodVersion

  /**
   * Callback when the card is clicked.
   */
  onAction?: () => unknown

  /**
   * The action component (e.g., dropdown menu).
   */
  action?: ReactNode

  /**
   * Whether the card is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether the method is the current one.
   */
  isCurrent?: boolean
}

/**
 * Container that renders the appropriate tax refund method card based on type.
 * Acts as a factory to select the correct card component.
 */
export const TaxRefundMethodCardListItemContainer = observer(
  function TaxRefundMethodCardListItemContainer({
    method,
    onAction,
    action,
    isDisabled,
    isCurrent,
  }: TaxRefundMethodCardListItemContainerProps) {
    switch (method.deliveryMethod.type) {
      case 'DirectDeposit': {
        const accountType =
          method.deliveryMethod.bankAccount.accountType === 'Checking'
            ? 'checking'
            : 'savings'

        return (
          <DirectDepositMethodCardListItem
            accountNumber={method.deliveryMethod.bankAccount.accountNumber}
            accountType={accountType}
            onAction={onAction}
            action={action}
            isDisabled={isDisabled}
            isCurrent={isCurrent}
          />
        )
      }
      case 'Check': {
        const address = method.deliveryMethod.mailingAddress

        return (
          <CheckMethodCardListItem
            addressLine1={address?.addressLine1 ?? null}
            addressLine2={address?.addressLine2 ?? null}
            city={address?.city ?? null}
            region={address?.region ?? null}
            postalCode={address?.postalCode ?? null}
            country={address?.country ?? null}
            onAction={onAction}
            action={action}
            isDisabled={isDisabled}
            isCurrent={isCurrent}
          />
        )
      }
      case 'FutureTaxCredit':
        return (
          <FutureTaxCreditMethodCardListItem
            onAction={onAction}
            action={action}
            isDisabled={isDisabled}
            isCurrent={isCurrent}
          />
        )
      default:
        return null
    }
  },
)

/**
 * Props for {@link OtherMethodCardListItemContainer}.
 */
export interface OtherMethodCardListItemContainerProps {
  /**
   * The tax refund method.
   */
  method: TaxRefundMethodVersion

  /**
   * Callback to select the method.
   */
  onSelect: () => unknown

  /**
   * Callback to delete the method.
   */
  onDelete: () => unknown

  /**
   * Whether actions are disabled.
   */
  isDisabled?: boolean
}

/**
 * Container for other method cards that manages its own dropdown state.
 */
export const OtherMethodCardListItemContainer = observer(
  function OtherMethodCardListItemContainer({
    method,
    onSelect,
    onDelete,
    isDisabled,
  }: OtherMethodCardListItemContainerProps) {
    const openDropdown$ = useSubject<void>()

    return (
      <TaxRefundMethodCardListItemContainer
        method={method}
        onAction={() => openDropdown$.next()}
        action={
          <OtherMethodDropdown
            open$={openDropdown$}
            onSelect={onSelect}
            onDelete={onDelete}
            isDisabled={isDisabled}
          />
        }
        isDisabled={isDisabled}
      />
    )
  },
)

interface ProvidedTaxRefundMethodCardListEmptyStateContainerProps {
  vm:
    | PersonalTaxRefundMethodSupportingItemViewModel
    | BusinessTaxRefundMethodSupportingItemViewModel
  action: ReactNode
}

/**
 * Provided Tax Refund Method Card List Empty State Container
 */
export const ProvidedTaxRefundMethodCardListEmptyStateContainer = observer(
  function ProvidedTaxRefundMethodCardListEmptyStateContainer({
    vm,
    action,
  }: ProvidedTaxRefundMethodCardListEmptyStateContainerProps) {
    const hasExistingMethods = vm.otherAvailableMethods.length > 0
    return (
      <ProvidedTaxRefundMethodCardListEmptyState
        action={action}
        hasExistingMethods={hasExistingMethods}
      />
    )
  },
)
